import React, { useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { get, isEmpty } from "lodash";
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Collapse,
  IconButton,
  Paper,
} from "@material-ui/core";
import KeyboardArrowDown from "@material-ui/icons/KeyboardArrowDown";
import KeyboardArrowUp from "@material-ui/icons/KeyboardArrowUp";
import { ContentFinderContext } from "../../context/ContentFinderContext";
import LoadingSpinner from "../common/LoadingSpinner";
import { PageErrorContent } from "../page-error/PageError";
import ContentFinderTitleBar from "./ContentFinderTitleBar";
import VersionDrawer from "./feature-details/VersionDrawer";
import {
  CONTENT_FINDER,
  FEATURE_VERSION_COLUMNS,
  NO_VERSIONS_ERROR,
} from "../../constants/ContentFinder";
import { PAGE_ERROR } from "../../constants/MediaSearch";
import {
  buildColumnValues,
  getMeta,
  getQualifiedMasterTracks,
} from "../../utils/ContentFinderUtils";
import "../../styles/contentFinder.scss";
import "../dashboard/table.scss";

const ContentFinderFeatureDetails = () => {
  const { titleId } = useParams<{ titleId: string }>();
  const {
    titleDetails,
    titleDetailsLoading,
    titleDetailsError,
    fetchTitleDetails,
  } = useContext(ContentFinderContext);
  const [openVersionId, setOpenVersionId] = useState<string | null>(null);
  const [selectedTable, setSelectedTable] = useState("versions");

  useEffect(() => {
    if (!titleId) return;
    const loadedTitleId = String(
      get(titleDetails, "[0].featureDetails[0].TitleId", "")
    );
    if (!titleDetailsLoading && loadedTitleId !== String(titleId)) {
      fetchTitleDetails(titleId, "feature");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [titleId]);

  const handleToggle = (versionId: string) => {
    setOpenVersionId((prev) => (prev === versionId ? null : versionId));
  };

  if (titleDetailsLoading) {
    return <LoadingSpinner show={true} />;
  }

  if (titleDetailsError) {
    return (
      <div className="series-details-page">
        <div className="hbo-promo">
          <div className="hbo-promo-error">
            <PageErrorContent
              title={PAGE_ERROR.title}
              message={PAGE_ERROR.message}
              reloadBtn={false}
            />
          </div>
        </div>
      </div>
    );
  }

  const responseObj = titleDetails?.length > 0 ? titleDetails[0] : {};
  const featureDetailsObj = get(responseObj, "featureDetails[0]", {});
  const titleBarDetails = !isEmpty(featureDetailsObj) ? featureDetailsObj : {};
  const allVersions = get(responseObj, "all", []);
  const metadataById = get(responseObj, "metadataById", {});
  const linksById = get(responseObj, "linksById", {});

  const versions = allVersions
    .filter((version) => {
      const importType = get(metadataById, [version.id, "stuff", "importType"]);
      return (
        (
          version.type === "content-version" &&
          importType !== "ESSENCE_INGEST"
        ) ||
        version.type === "qualified-version"
      );
    })
    .map((version) => {
      const meta = getMeta(metadataById, version.id);
      const qmTracks = getQualifiedMasterTracks(
        metadataById,
        linksById,
        version.id
      );
      return {
        id: version.id,
        type: version.type,
        cid: get(meta, "cid", "-"),
        columns: buildColumnValues(meta, qmTracks),
        meta,
        qmTracks,
      };
    });

  const versionCount = versions.filter(
    (v) => v.type === "content-version"
  ).length;

  const masterCount = versions.filter(
    (v) => v.type === "qualified-version"
  ).length;

  const displayedVersions = versions.filter((version) =>
    selectedTable === "versions"
      ? version.type === "content-version"
      : version.type === "qualified-version"
  );

  const tableBodyId =
    selectedTable === "versions"
      ? "table_container_content"
      : "table_container_master";


  return (
    <div className="series-details-page">
      {!isEmpty(titleBarDetails) && (
        <ContentFinderTitleBar titleDetails={titleBarDetails} />
      )}

      <div className="contentFinderSeriesGrid contentFinderSeriesGrid--no-pagination">
        {isEmpty(versions) ? (
          <div className="hbo-promo">
            <div className="hbo-promo-error">
              <PageErrorContent
                title={NO_VERSIONS_ERROR.title}
                message={NO_VERSIONS_ERROR.message}
                reloadBtn={false}
              />
            </div>
          </div>
        ) : (
          <>
            <div className="contentFinderSeriesTableInfoContainer">
              <button
                type="button"
                className={`contentFinderSeriesTableInfo ${selectedTable === "versions" ? "active" : ""
                  }`}
                onClick={() => setSelectedTable("versions")}
              >
                {`${CONTENT_FINDER.versionsPage} (${versionCount})`}
              </button>

              <button
                type="button"
                className={`contentFinderSeriesTableInfo ${selectedTable === "masters" ? "active" : ""
                  }`}
                onClick={() => setSelectedTable("masters")}
              >
                {`${CONTENT_FINDER.mastersPage} (${masterCount})`}
              </button>
            </div>
            <TableContainer
              component={Paper}
              className="content-finder-table-version"
            >
              <Table
                aria-label="feature versions table"
                className="content-finder-table content-finder-table--no-scroll"
              >
                <TableHead className="tableHeader content-finder-table-head">
                  <TableRow className="tableRow content-finder-header-row">
                    {FEATURE_VERSION_COLUMNS.map((column, index) => (
                      <TableCell
                        key={`header_${index}`}
                        className="media-cell"
                        style={{ width: column.width }}
                      >
                        {column.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody id={tableBodyId} className="content-finder-table-body">
                  {displayedVersions.map((version) => {
                    const isOpen = openVersionId === version.id;

                    return (
                      <React.Fragment key={version.id}>
                        <TableRow
                          className={`content-finder-body-row version-data-row version-title-row${isOpen ? " version-open" : ""
                            }`}
                        >
                          {FEATURE_VERSION_COLUMNS.map((column) => (
                            <TableCell
                              key={column.id}
                              className="content-finder-table-cell"
                              style={{ width: column.width }}
                            >
                              {column.id === "cid" ? (
                                <span
                                  className="version-cid-cell"
                                  onClick={() => handleToggle(version.id)}
                                >
                                  <IconButton
                                    aria-label="expand version row"
                                    size="small"
                                    className="version-expander"
                                  >
                                    {isOpen ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                  </IconButton>

                                  <span className="version-slug">{version.cid}</span>
                                </span>
                              ) : (
                                version.columns[column.id]
                              )}
                            </TableCell>
                          ))}
                        </TableRow>

                        <TableRow className="content-finder-body-row version-drawer-row">
                          <TableCell
                            className="version-drawer-cell"
                            colSpan={FEATURE_VERSION_COLUMNS.length}
                          >
                            <Collapse in={isOpen} timeout="auto" unmountOnExit>
                              <VersionDrawer
                                meta={version.meta}
                                qmTracks={version.qmTracks}
                                titleId={get(featureDetailsObj, "TitleId", titleId)}
                              />
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

          </>
        )}
      </div>
    </div>
  );
};

export default ContentFinderFeatureDetails;
